// Service for fetching and parsing property data from BuySellCyprus.com

import { createNormalizedPropertyData } from './propertyDataSchema';
import { fetchViaProxies, isNotFoundError } from './proxyFetch';

/**
 * Sanitize text extracted from HTML to prevent injection and remove garbage.
 * - Strips any remaining HTML tags
 * - Removes script/style content patterns
 * - Collapses whitespace
 * - Trims to a reasonable length
 * @param {string} text - Raw extracted text
 * @param {number} maxLength - Maximum allowed length (default 2000)
 * @returns {string} - Clean text
 */
export const sanitizeText = (text, maxLength = 2000) => {
  if (!text) return '';
  return text
    // Remove anything that looks like HTML tags
    .replace(/<[^>]*>/g, '')
    // Remove common script patterns
    .replace(/(?:window\.|document\.|function\s*\(|var\s+\w+\s*=|const\s+\w+\s*=|let\s+\w+\s*=|if\s*\(typeof|\.push\(|\.createElement|\.appendChild|\.innerHTML|dataLayer|gtag|addEventListener)\S*/g, '')
    // Remove CSS patterns
    .replace(/\{[^}]*\}/g, '')
    // Collapse whitespace
    .replace(/\s+/g, ' ')
    .trim()
    .substring(0, maxLength);
};

/**
 * Remove script, style, footer, nav, and noscript elements from a parsed document.
 * This creates a clean document for text extraction.
 * @param {Document} doc - Parsed HTML document
 * @returns {Document} - Same document with junk elements removed
 */
const cleanDocument = (doc) => {
  const selectorsToRemove = [
    'script', 'style', 'noscript', 'footer', 'nav',
    'iframe', '[type="application/ld+json"]'
  ];
  for (const selector of selectorsToRemove) {
    const elements = doc.querySelectorAll(selector);
    for (const el of elements) {
      el.remove();
    }
  }
  return doc;
};

/**
 * Detect Cloudflare "Just a moment..." challenge pages that proxies can't solve.
 * These return HTTP 200 but contain a JS challenge instead of real content.
 *
 * Only checks for `cf_chl_opt` (the challenge config object) and the
 * "Just a moment" + "Enable JavaScript" combo.  The string
 * "challenge-platform" also appears in Cloudflare's *normal* monitoring
 * scripts on real pages, so matching it causes false positives.
 */
const isCloudflareChallenge = (html) => {
  if (html.includes('cf_chl_opt')) return true;
  if (html.includes('Just a moment') && html.includes('Enable JavaScript')) return true;
  return false;
};

/**
 * Fetch HTML content through the Cloudflare Worker proxy or fallback CORS proxies.
 * Validates that the response is real HTML (not a Cloudflare challenge page).
 * @param {string} url - Target URL to fetch
 * @returns {Promise<string>} - HTML content
 */
export const fetchHtmlViaProxy = async (url) => {
  try {
    return await fetchViaProxies(url, {
      // The worker may need to drive headless Chrome through a Cloudflare
      // "Just a moment..." challenge (waits ~12s for it to auto-solve), so
      // allow enough time before aborting and falling through to CORS proxies.
      timeout: 35000,
      responseType: 'text',
      label: 'BuySell',
      validate: (html) => {
        if (!html || html.length < 500) return false;
        if (isCloudflareChallenge(html)) return false;
        return true;
      },
    });
  } catch (error) {
    // The listing is gone (proxy saw a 404 or a redirect to the homepage) —
    // let callers turn this into "expired" rather than "proxy down".
    if (isNotFoundError(error)) throw error;
    throw new Error(
      'All proxy services are currently unavailable. ' +
      'Please try again later or input property data manually.'
    );
  }
};

/**
 * Parse the <title> tag to extract structured info.
 * Title format: "3 Bedroom Detached house for sale in Sea Caves Peyia (ID:7926498) - €1,290,000 - BuySellCyprus.com"
 * @param {Document} doc - Parsed HTML document
 * @returns {Object} - { title, price, location, listingId, forSale, forRent }
 */
const parsePageTitle = (doc) => {
  const result = { title: '', price: 0, location: '', listingId: '', forSale: false, forRent: false };

  const titleEl = doc.querySelector('title');
  if (!titleEl) return result;

  const fullTitle = titleEl.textContent.trim();

  // Extract listing ID: (ID:7926498)
  const idMatch = fullTitle.match(/\(ID[:\s]*(\d+)\)/i);
  if (idMatch) {
    result.listingId = idMatch[1];
  }

  // Extract price: €1,290,000
  const priceMatch = fullTitle.match(/€\s?([\d,]+)/);
  if (priceMatch) {
    result.price = parseFloat(priceMatch[1].replace(/,/g, ''));
  }

  // Extract property title (everything before "for sale/rent in" or "(ID:")
  const titleMatch = fullTitle.match(/^(.+?)\s+(?:for\s+(?:sale|rent)\s+in|in\s+|\(ID)/i);
  if (titleMatch) {
    result.title = titleMatch[1].trim();
  }

  // Extract location: between "in {Location}" and "(ID:"
  const locationMatch = fullTitle.match(/(?:for\s+(?:sale|rent)\s+)?in\s+(.+?)\s*\(ID/i);
  if (locationMatch) {
    result.location = locationMatch[1].trim();
  }

  // Detect sale/rent
  const titleLower = fullTitle.toLowerCase();
  if (titleLower.includes('for sale') || titleLower.includes('for-sale')) {
    result.forSale = true;
  }
  if (titleLower.includes('for rent') || titleLower.includes('for-rent') || titleLower.includes('to rent')) {
    result.forRent = true;
  }

  // Default to sale if neither detected
  if (!result.forSale && !result.forRent) {
    result.forSale = true;
  }

  return result;
};

/**
 * Parse key features from the page using multiple strategies.
 * Returns structured data AND the full list of feature bullet points.
 * @param {Document} doc - Parsed HTML document (should be cleaned)
 * @returns {Object} - { bedrooms, bathrooms, area, plotSize, listingId, featuresList }
 */
const parseKeyFeatures = (doc) => {
  const result = { bedrooms: '', bathrooms: '', area: '', plotSize: '', listingId: '', featuresList: [] };

  // Collect all feature bullet points from the "Key features" section.
  // These contain the richest property details on BuySellCyprus.
  const bodyText = doc.body ? doc.body.textContent : doc.documentElement.textContent;

  // Also try to find <li> items that look like key features
  const allLis = doc.querySelectorAll('li');
  const seenFeatures = new Set();

  for (const li of allLis) {
    const text = li.textContent.trim();
    if (!text || text.length < 3 || text.length > 200) continue;

    // Skip items that are clearly navigation/footer links
    if (text.match(/^(Resale|New Homes|Bank Owned|Total|Properties Sold|Cookie|Terms|Privacy|Sitemap|Subscribe|Browse|Cheap|Quick Links)/i)) continue;
    // Skip items with URLs
    if (text.match(/^https?:\/\//)) continue;

    // Extract structured data from feature items
    const bedroomMatch = text.match(/Bedrooms?\s*:\s*(\d+)/i);
    if (bedroomMatch && !result.bedrooms) result.bedrooms = bedroomMatch[1];

    const bathroomMatch = text.match(/Bathrooms?\s*:\s*(\d+)/i);
    if (bathroomMatch && !result.bathrooms) result.bathrooms = bathroomMatch[1];

    const areaMatch = text.match(/(?:Total\s+)?[Cc]overed\s+area\s*:\s*([\d,]+)\s*(?:sqm|m²)/i);
    if (areaMatch && !result.area) result.area = areaMatch[1].replace(/,/g, '');

    if (!result.area) {
      const altAreaMatch = text.match(/(?:Internal|Living)\s+area\s*:\s*([\d,]+)\s*(?:sqm|m²)/i);
      if (altAreaMatch) result.area = altAreaMatch[1].replace(/,/g, '');
    }

    const plotMatch = text.match(/Plot(?:\s+area)?\s*:\s*([\d,]+)\s*(?:sqm|m²)/i);
    if (plotMatch && !result.plotSize) result.plotSize = plotMatch[1].replace(/,/g, '');

    const idMatch = text.match(/Listing\s+ID\s*:\s*(\d+)/i);
    if (idMatch && !result.listingId) result.listingId = idMatch[1];

    // Collect as a feature bullet if it looks like a property feature
    // (not a duplicate of bedrooms/bathrooms/area/listing ID which we already extract as fields)
    const isDuplicateField = bedroomMatch || bathroomMatch || areaMatch || plotMatch || idMatch;
    if (!isDuplicateField && !seenFeatures.has(text.toLowerCase())) {
      seenFeatures.add(text.toLowerCase());
      result.featuresList.push(text);
    }
  }

  // Fallback: regex search on body text for structured fields
  if (bodyText) {
    if (!result.bedrooms) {
      const m = bodyText.match(/Bedrooms?\s*:\s*(\d+)/i);
      if (m) result.bedrooms = m[1];
    }
    if (!result.bathrooms) {
      const m = bodyText.match(/Bathrooms?\s*:\s*(\d+)/i);
      if (m) result.bathrooms = m[1];
    }
    if (!result.area) {
      const m = bodyText.match(/(?:Total\s+)?[Cc]overed\s+area\s*:\s*([\d,]+)\s*(?:sqm|m²)/i);
      if (m) result.area = m[1].replace(/,/g, '');
    }
    if (!result.area) {
      const m = bodyText.match(/(?:Internal|Living)\s+area\s*:\s*([\d,]+)\s*(?:sqm|m²)/i);
      if (m) result.area = m[1].replace(/,/g, '');
    }
    if (!result.plotSize) {
      const m = bodyText.match(/Plot(?:\s+area)?\s*:\s*([\d,]+)\s*(?:sqm|m²)/i);
      if (m) result.plotSize = m[1].replace(/,/g, '');
    }
    if (!result.listingId) {
      const m = bodyText.match(/Listing\s+ID\s*:\s*(\d+)/i);
      if (m) result.listingId = m[1];
    }
  }

  // Last resort: extract bedrooms from <title> tag
  if (!result.bedrooms) {
    const titleEl = doc.querySelector('title');
    if (titleEl) {
      const titleMatch = titleEl.textContent.match(/(\d+)\s+Bedroom/i);
      if (titleMatch) result.bedrooms = titleMatch[1];
    }
  }

  return result;
};

/**
 * Parse the price from the page content (fallback if title parsing failed)
 * @param {Document} doc - Parsed HTML document
 * @returns {number} - Price in euros
 */
const parsePrice = (doc) => {
  // Try to find the main price element - usually in an h2 near the top
  const h2s = doc.querySelectorAll('h2');
  for (const h2 of h2s) {
    const text = h2.textContent.trim();
    const priceMatch = text.match(/€\s?([\d,]+)/);
    if (priceMatch) {
      return parseFloat(priceMatch[1].replace(/,/g, ''));
    }
  }

  // Also check for prices in strong/span elements
  const priceElements = doc.querySelectorAll('.price, [class*="price"]');
  for (const el of priceElements) {
    const text = el.textContent.trim();
    const priceMatch = text.match(/€\s?([\d,]+)/);
    if (priceMatch) {
      return parseFloat(priceMatch[1].replace(/,/g, ''));
    }
  }

  return 0;
};

/**
 * Parse the description section.
 * Uses a cleaned copy of the document to avoid picking up scripts/footer/nav.
 * @param {Document} doc - Parsed HTML document (will NOT be modified)
 * @param {Document} cleanedDoc - Document with scripts/footer/nav removed
 * @returns {string} - Description text
 */
const parseDescription = (doc, cleanedDoc) => {
  // Strategy 1: DOM-based on cleaned doc - find "Description" heading,
  // then collect text from subsequent elements until the next heading.
  const headings = cleanedDoc.querySelectorAll('h2, h3, h4');
  for (const h of headings) {
    if (h.textContent.trim().toLowerCase() === 'description') {
      const texts = [];

      // Walk through subsequent elements in the DOM tree
      // Try parent's children first (handles wrapper divs)
      const parent = h.parentElement;
      if (parent) {
        let foundHeading = false;
        for (const child of parent.children) {
          if (child === h) {
            foundHeading = true;
            continue;
          }
          if (!foundHeading) continue;
          // Stop at next heading
          if (/^H[2-4]$/i.test(child.tagName)) break;
          const text = child.textContent?.trim();
          if (text && text.length > 3) texts.push(text);
        }
      }

      // Also try nextSibling walking (in case parent is the heading's direct wrapper)
      if (texts.length === 0) {
        let el = h.nextElementSibling;
        while (el) {
          if (/^H[2-4]$/i.test(el.tagName)) break;
          const text = el.textContent?.trim();
          if (text && text.length > 3) texts.push(text);
          el = el.nextElementSibling;
        }
      }

      if (texts.length > 0) {
        const desc = texts.join(' ')
          .replace(/Read\s*More\s*Show\s*Less/gi, '')
          .replace(/\s+/g, ' ')
          .trim();
        if (desc.length > 10) return sanitizeText(desc, 2000);
      }
    }
  }

  // Strategy 2: Full-text regex on cleaned body text (no scripts/footer)
  const bodyText = cleanedDoc.body ? cleanedDoc.body.textContent : '';
  if (bodyText) {
    // More conservative regex: stop at known section boundaries
    const descMatch = bodyText.match(
      /\bDescription\b\s+([\s\S]{10,1500}?)(?:\s*(?:Buying\s+expenses|Stamp\s+duties|Properties\s+around|Customers\s+who|Disclaimer|Related\s+properties|Similar\s+properties|Check\s+the\s+Value|View\s+sold))/i
    );
    if (descMatch && descMatch[1]) {
      const desc = descMatch[1]
        .replace(/Read\s*More\s*Show\s*Less/gi, '')
        .replace(/\s+/g, ' ')
        .trim();
      if (desc.length > 10) return sanitizeText(desc, 2000);
    }
  }

  // Strategy 3: Look for meta description tag (original doc, not cleaned)
  const metaDesc = doc.querySelector('meta[name="description"]');
  if (metaDesc) {
    const content = metaDesc.getAttribute('content');
    if (content && content.length > 20) return sanitizeText(content, 2000);
  }

  return '';
};

/**
 * Parse published/updated dates from structured data or meta tags.
 * BuySellCyprus may embed JSON-LD schema.org data or og/meta date tags.
 * @param {Document} doc - Parsed HTML document
 * @returns {Object} - { publishedDate, updatedDate }
 */
const parseDates = (doc) => {
  const result = { publishedDate: '', updatedDate: '' };

  // Strategy 1: Check for JSON-LD structured data (schema.org)
  const ldScripts = doc.querySelectorAll('script[type="application/ld+json"]');
  for (const script of ldScripts) {
    try {
      const data = JSON.parse(script.textContent);
      // Handle both single objects and arrays
      const items = Array.isArray(data) ? data : [data];
      for (const item of items) {
        // datePosted is the standard for RealEstateListing
        if (item.datePosted) {
          result.publishedDate = formatIsoDate(item.datePosted);
        }
        if (item.datePublished) {
          result.publishedDate = result.publishedDate || formatIsoDate(item.datePublished);
        }
        if (item.dateModified) {
          result.updatedDate = formatIsoDate(item.dateModified);
        }
        // Check nested @graph structure
        if (item['@graph'] && Array.isArray(item['@graph'])) {
          for (const node of item['@graph']) {
            if (node.datePosted) result.publishedDate = result.publishedDate || formatIsoDate(node.datePosted);
            if (node.datePublished) result.publishedDate = result.publishedDate || formatIsoDate(node.datePublished);
            if (node.dateModified) result.updatedDate = result.updatedDate || formatIsoDate(node.dateModified);
          }
        }
      }
    } catch (e) {
      // Invalid JSON-LD, skip
    }
  }

  // Strategy 2: Check meta tags
  if (!result.publishedDate) {
    const metaPublished = doc.querySelector(
      'meta[property="article:published_time"], meta[name="date"], meta[name="DC.date.issued"]'
    );
    if (metaPublished) {
      result.publishedDate = formatIsoDate(metaPublished.getAttribute('content'));
    }
  }
  if (!result.updatedDate) {
    const metaModified = doc.querySelector(
      'meta[property="article:modified_time"], meta[property="og:updated_time"]'
    );
    if (metaModified) {
      result.updatedDate = formatIsoDate(metaModified.getAttribute('content'));
    }
  }

  return result;
};

/**
 * Format an ISO date string to YYYY-MM-DD
 * @param {string} dateStr - Date string in any parseable format
 * @returns {string} - Formatted date or empty string
 */
const formatIsoDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    if (!isNaN(d.getTime())) {
      return d.toISOString().split('T')[0]; // YYYY-MM-DD
    }
  } catch (e) {
    // ignore
  }
  return '';
};

/**
 * Parse the agent/developer name
 * @param {Document} doc - Parsed HTML document
 * @returns {string} - Agent name
 */
const parseAgentName = (doc) => {
  // The agent name is typically in an h3 following an agent image/link
  const agentLinks = doc.querySelectorAll('a[href*="estate-agent"], a[href*="properties-for-sale/estate-agent"]');
  for (const link of agentLinks) {
    // Check if a nearby h3 has the agent name
    const parent = link.closest('div') || link.parentElement;
    if (parent) {
      const h3 = parent.querySelector('h3');
      if (h3 && h3.textContent.trim() && !h3.textContent.includes('Save') && !h3.textContent.includes('Report')) {
        return h3.textContent.trim();
      }
    }
  }

  // Fallback: look for h3 elements that contain agent-like names
  const h3s = doc.querySelectorAll('h3');
  for (const h3 of h3s) {
    const text = h3.textContent.trim();
    // Agent names are usually followed by "Agent reg. number" text
    const next = h3.nextElementSibling || h3.parentElement?.nextElementSibling;
    if (next && next.textContent.toLowerCase().includes('agent')) {
      return text;
    }
  }

  return '';
};

/**
 * Parse image URLs from the page.
 * BuySellCyprus uses lazy loading with placeholder SVGs.
 * Real images are in data-src attributes or can be derived from image IDs.
 * @param {Document} doc - Parsed HTML document
 * @returns {string} - First real image URL, or empty string
 */
const parseImageUrl = (doc) => {
  // Try og:image meta tag first (most reliable)
  const ogImage = doc.querySelector('meta[property="og:image"]');
  if (ogImage) {
    const url = ogImage.getAttribute('content');
    if (url && !url.includes('whiteimg.svg') && !url.includes('placeholder')) {
      return url;
    }
  }

  // Try data-src attributes on images (lazy-loaded images)
  const imgs = doc.querySelectorAll('img[data-src]');
  for (const img of imgs) {
    const dataSrc = img.getAttribute('data-src');
    if (dataSrc && !dataSrc.includes('whiteimg.svg') && !dataSrc.includes('placeholder') && !dataSrc.includes('logo')) {
      return dataSrc.startsWith('http') ? dataSrc : `https://www.buysellcyprus.com${dataSrc}`;
    }
  }

  // Try srcset on images
  const imgsWithSrcset = doc.querySelectorAll('img[srcset]');
  for (const img of imgsWithSrcset) {
    const srcset = img.getAttribute('srcset');
    if (srcset && !srcset.includes('whiteimg') && !srcset.includes('placeholder')) {
      // Take the first URL from srcset
      const firstUrl = srcset.split(',')[0].trim().split(' ')[0];
      if (firstUrl) {
        return firstUrl.startsWith('http') ? firstUrl : `https://www.buysellcyprus.com${firstUrl}`;
      }
    }
  }

  // Fallback: try to find any image with property-related alt text
  const allImgs = doc.querySelectorAll('img');
  for (const img of allImgs) {
    const src = img.getAttribute('src') || '';
    const alt = img.getAttribute('alt') || '';
    if (alt.toLowerCase().includes('bedroom') || alt.toLowerCase().includes('property')) {
      if (src && !src.includes('whiteimg.svg') && !src.includes('placeholder') && !src.includes('logo')) {
        return src.startsWith('http') ? src : `https://www.buysellcyprus.com${src}`;
      }
    }
  }

  return '';
};

/**
 * Extract property ID from a BuySellCyprus URL
 * Supported formats:
 *   /property/7926498
 *   /property-for-sale/paphos/.../slug-7926498
 *   /property-for-rent/.../.../slug-7926498
 * @param {string} url - BuySellCyprus URL
 * @returns {string|null} - Property ID
 */
export const extractBuySellId = (url) => {
  if (!url) return null;

  // /property/{id}
  const directMatch = url.match(/\/property\/(\d+)/i);
  if (directMatch) return directMatch[1];

  // slug-{id} at end of URL path
  const slugMatch = url.match(/(\d{5,})(?:\/(?:gallery)?)?(?:\?.*)?$/);
  if (slugMatch) return slugMatch[1];

  return null;
};

/**
 * Construct the canonical property page URL for a BuySellCyprus property
 * @param {string} propertyId - Numeric property ID
 * @returns {string} - Canonical URL
 */
const getCanonicalUrl = (propertyId) => {
  return `https://www.buysellcyprus.com/property/${propertyId}`;
};

/**
 * Fetch and parse a BuySellCyprus property page, returning normalized data
 * @param {string} propertyUrl - BuySellCyprus property URL
 * @returns {Promise<Object>} - Normalized property data
 */
export const fetchBuySellData = async (propertyUrl) => {
  if (!propertyUrl || !propertyUrl.trim()) {
    throw new Error("Property URL is required");
  }

  const propertyId = extractBuySellId(propertyUrl);
  if (!propertyId) {
    throw new Error("Invalid BuySellCyprus URL: Cannot extract property ID");
  }

  // Always use the canonical /property/{id} URL for fetching
  const fetchUrl = getCanonicalUrl(propertyId);

  const notFoundMessage =
    `Property #${propertyId} not found on BuySellCyprus. ` +
    'It may have been removed or is no longer active.';

  let html;
  try {
    html = await fetchHtmlViaProxy(fetchUrl);
  } catch (error) {
    if (isNotFoundError(error)) throw new Error(notFoundMessage);
    throw error;
  }

  // Parse the HTML using DOMParser (available in browser)
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  // BuySell answers a removed listing with a redirect to the homepage. If a
  // proxy followed it without flagging, we get the homepage here: no
  // "(ID:xxx)" in the <title>, no price. Treat that as not found rather than
  // saving an empty record.
  const preTitle = parsePageTitle(doc);
  if (!preTitle.listingId && !preTitle.price) {
    throw new Error(notFoundMessage);
  }

  // Extract dates and structured data BEFORE cleaning (needs script tags)
  const dates = parseDates(doc);

  // Create a cleaned copy of the document for text extraction
  // (removes scripts, styles, footer, nav to prevent garbage in text)
  const cleanedDoc = parser.parseFromString(html, 'text/html');
  cleanDocument(cleanedDoc);

  // Extract all data from the parsed HTML
  const titleInfo = preTitle;
  const keyFeatures = parseKeyFeatures(cleanedDoc);
  const description = parseDescription(doc, cleanedDoc);
  const agentName = parseAgentName(doc);
  const imageUrl = parseImageUrl(doc);
  const fallbackPrice = parsePrice(cleanedDoc);

  // Use the best available data, sanitize all text fields
  const price = titleInfo.price || fallbackPrice;
  const title = sanitizeText(titleInfo.title || '', 200);
  const location = sanitizeText(titleInfo.location || '', 200);

  // Build a rich description combining the ad description + key feature highlights
  let fullDescription = '';
  if (description) {
    fullDescription = description;
  }
  if (keyFeatures.featuresList && keyFeatures.featuresList.length > 0) {
    const featuresText = keyFeatures.featuresList
      .map(f => sanitizeText(f, 200))
      .filter(f => f.length > 0)
      .map(f => `• ${f}`)
      .join('\n');
    if (featuresText) {
      fullDescription = fullDescription
        ? `${fullDescription}\n\nKey features:\n${featuresText}`
        : `Key features:\n${featuresText}`;
    }
  }

  // Detect property type from URL
  const urlLower = propertyUrl.toLowerCase();
  let forSale = titleInfo.forSale;
  let forRent = titleInfo.forRent;
  if (urlLower.includes('property-for-sale') || urlLower.includes('properties-for-sale')) {
    forSale = true;
  }
  if (urlLower.includes('property-for-rent') || urlLower.includes('properties-for-rent') || urlLower.includes('property-to-rent')) {
    forRent = true;
  }
  if (!forSale && !forRent) {
    forSale = true;
  }

  return createNormalizedPropertyData({
    title,
    description: sanitizeText(fullDescription, 3000),
    price,
    publishedDate: dates.publishedDate || '',
    updatedDate: dates.updatedDate || '',
    area: sanitizeText(keyFeatures.area || '', 50),
    bedrooms: sanitizeText(keyFeatures.bedrooms || '', 10),
    bathrooms: sanitizeText(keyFeatures.bathrooms || '', 10),
    imageURL: imageUrl,
    location,
    landlordName: sanitizeText(agentName, 200),
    forSale,
    forRent,
  });
};

/**
 * Detect property type for a BuySellCyprus URL
 * @param {string} url - Property URL
 * @param {Object} normalizedData - Already-normalized data (optional, has forSale/forRent)
 * @returns {Object} - { forSale, forRent }
 */
export const detectBuySellPropertyType = (url, normalizedData = null) => {
  if (normalizedData && (normalizedData.forSale || normalizedData.forRent)) {
    return { forSale: normalizedData.forSale, forRent: normalizedData.forRent };
  }

  const urlLower = (url || '').toLowerCase();
  let forSale = false;
  let forRent = false;

  if (urlLower.includes('for-sale') || urlLower.includes('for sale')) {
    forSale = true;
  }
  if (urlLower.includes('for-rent') || urlLower.includes('for rent') || urlLower.includes('to-rent') || urlLower.includes('to rent')) {
    forRent = true;
  }
  if (!forSale && !forRent) {
    forSale = true;
  }

  return { forSale, forRent };
};
